import type { TransitionEasing } from './infoserviceSlideshowApi.js'

/**
 * Courbes d'accélération des transitions du diaporama.
 *
 * CasparCG ne joue pas de courbe de Bézier libre : seulement ses courbes
 * nommées (`EASEINOUTSINE`…). L'écran les décrit en deux choix — l'intensité
 * et le profil — et dessine chacune avec la courbe de Bézier cubique qui la
 * reproduit (valeurs de référence easings.net), la même que celle de l'aperçu
 * animé. Ce qu'on voit est donc ce que jouera l'antenne.
 */

export type EasingCurve = 'LINEAR' | 'SINE' | 'CUBIC' | 'EXPO'
export type EasingProfile = 'IN' | 'OUT' | 'IN_OUT'

export const EASING_CURVE_OPTIONS: readonly { value: EasingCurve; label: string; hint: string }[] = [
  { value: 'LINEAR', label: 'Linéaire', hint: 'Vitesse constante, du début à la fin.' },
  { value: 'SINE', label: 'Douce', hint: 'Accélération légère, presque imperceptible.' },
  { value: 'CUBIC', label: 'Marquée', hint: 'Mouvement franchement amorti.' },
  { value: 'EXPO', label: 'Forte', hint: 'Départ ou arrivée très lents, milieu très rapide.' },
]

export const EASING_PROFILE_OPTIONS: readonly { value: EasingProfile; label: string; hint: string }[] = [
  { value: 'IN_OUT', label: 'Ease in & out', hint: 'Démarre et arrive en douceur.' },
  { value: 'OUT', label: 'Ease out', hint: 'Part vite, arrive en douceur.' },
  { value: 'IN', label: 'Ease in', hint: 'Démarre en douceur, arrive vite.' },
]

/** Courbe conseillée quand on quitte « Linéaire » : la plus naturelle à l'œil. */
export const DEFAULT_SMOOTH_EASING: TransitionEasing = 'IN_OUT_SINE'

export function easingParts(easing: TransitionEasing): { curve: EasingCurve; profile: EasingProfile } {
  if (easing === 'LINEAR') return { curve: 'LINEAR', profile: 'IN_OUT' }
  const match = /^(IN_OUT|IN|OUT)_(SINE|CUBIC|EXPO)$/.exec(easing)
  if (!match) return { curve: 'LINEAR', profile: 'IN_OUT' }
  return { curve: match[2] as EasingCurve, profile: match[1] as EasingProfile }
}

export function easingOf(curve: EasingCurve, profile: EasingProfile): TransitionEasing {
  return curve === 'LINEAR' ? 'LINEAR' : `${profile}_${curve}` as TransitionEasing
}

/** Points de contrôle (x1, y1, x2, y2) de la Bézier cubique équivalente. */
export const EASING_BEZIER: Record<TransitionEasing, readonly [number, number, number, number]> = {
  LINEAR: [0, 0, 1, 1],
  IN_SINE: [0.12, 0, 0.39, 0],
  OUT_SINE: [0.61, 1, 0.88, 1],
  IN_OUT_SINE: [0.37, 0, 0.63, 1],
  IN_CUBIC: [0.32, 0, 0.67, 0],
  OUT_CUBIC: [0.33, 1, 0.68, 1],
  IN_OUT_CUBIC: [0.65, 0, 0.35, 1],
  IN_EXPO: [0.7, 0, 0.84, 0],
  OUT_EXPO: [0.16, 1, 0.3, 1],
  IN_OUT_EXPO: [0.87, 0, 0.13, 1],
}

/** Valeur CSS `animation-timing-function` de l'aperçu. */
export function easingCss(easing: TransitionEasing): string {
  return easing === 'LINEAR' ? 'linear' : `cubic-bezier(${EASING_BEZIER[easing].join(', ')})`
}

/** Tracé SVG de la courbe dans une boîte `size` × `size` (y vers le haut). */
export function easingPath(easing: TransitionEasing, size: number): string {
  const [x1, y1, x2, y2] = EASING_BEZIER[easing]
  const px = (x: number) => Math.round(x * size * 10) / 10
  const py = (y: number) => Math.round((1 - y) * size * 10) / 10
  return `M ${px(0)} ${py(0)} C ${px(x1)} ${py(y1)}, ${px(x2)} ${py(y2)}, ${px(1)} ${py(1)}`
}
